/**
 * Setup candidate labelling store (research tool)
 *
 * Holds the ENGINE's diamonds, the engine's rule, and your hand labels.
 *
 * Nothing in here scores a bar. Both diamond sources are the engine — the
 * `diamond-signal` socket for bars that complete while the tab is open, and
 * `GET /api/diamonds/history` for everything older — and they write the same
 * key space, so a bar can never carry two opinions.
 *
 * Labels persist through the user-preferences API (Redis-backed), so they
 * survive reloads and can be pulled back out for offline analysis:
 *   curl -s localhost:9647/api/user/preferences | jq .setupLabels
 *
 * ZUSTAND RULE (repo-wide): any selector returning a NEW array/object MUST be
 * wrapped in useShallow. The diamond maps below are held as stable references
 * and replaced wholesale on write, so selecting one directly is safe; deriving
 * an ARRAY from them must happen in a useMemo outside the selector, or React
 * loops itself to death on error #185 and blanks the chart.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { getPreferences, updatePreferenceDirect } from '../hooks/usePreferences';
import { CONFIG } from '../config';
import {
  CandidateLabel,
  CandidateLabelMap,
  CandidateThresholds,
  CandidateVerdict,
  AutoTagOptions,
  DEFAULT_AUTO_TAG_OPTIONS,
  candidateId,
  normaliseVerdict,
  type DiamondHistoryResponse,
  type EngineDiamond,
  type EngineRejectedBar,
} from '../utils/setupCandidates';

export type { EngineDiamond, EngineRejectedBar } from '../utils/setupCandidates';

const ENABLED_KEY = 'setupCandidates.enabled';
const THRESHOLDS_KEY = 'setupCandidates.thresholds'; // legacy, cleared on boot
/** Set only by "reset to now", which pins an absolute cutoff. */
const SCORE_FROM_PINNED_KEY = 'setupCandidates.scoreFromPinned';
const AUTOTAG_KEY = 'setupCandidates.autoTag';
const AUTOTAG_ON_KEY = 'setupCandidates.autoTagEnabled';
const PANEL_KEY = 'setupCandidates.panelOpen';
const SCORE_FROM_KEY = 'setupCandidates.scoreFrom';
const LOOKBACK_KEY = 'setupCandidates.lookbackDays';
/** Generation stamp for the one-time corrections to the stored view window. */
const LOOKBACK_MIGRATION_KEY = 'setupCandidates.lookbackMigration';
/**
 * Current generation. Bump to ship ANOTHER one-time correction.
 *
 * A version rather than a boolean: a `didMigrate` flag is spent the moment it
 * is set, so the next correction needs a second key, and the one after that a
 * third. One monotonic slot keeps the whole history of corrections in one
 * readable place — and it is readable, which matters when someone asks in six
 * months why their window moved on its own.
 */
const LOOKBACK_MIGRATION_VERSION = 1;
/** `lookbackDays` meaning "everything the chart has loaded". */
const LOOKBACK_ALL = -1;
/**
 * How recent a pinned cutoff has to be for the migration to leave it alone.
 *
 * One day: the chart holds about three days of bars, so a pin from within the
 * last one is still inside the session you are working in and is plausibly the
 * deliberate "from here" it claims to be. Anything older predates the data on
 * screen and is indistinguishable from the stale-absolute-cutoff bug.
 */
const PIN_FRESH_MS = 86_400_000;

/**
 * Default cutoff: local midnight today.
 *
 * Cutting at the exact instant you enable it is the literal "no history", but
 * it leaves the chart empty until the next bar closes. Today's session drops
 * the multi-day backlog while still showing something to work with; "reset to
 * now" gives the hard cut when you want it.
 */
function startOfToday(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function loadEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * One-time cleanup: the rule used to live here. It is server-side now, and a
 * leftover copy would only confuse anyone reading localStorage to debug.
 */
function dropLegacyThresholdCache(): void {
  try {
    localStorage.removeItem(THRESHOLDS_KEY);
  } catch {
    // storage blocked — nothing to clean
  }
}

/**
 * Send the rule to the engine so the bot scores with the same thresholds the
 * chart draws with.
 *
 * These used to be localStorage-only: retuning the rule moved the diamonds on
 * screen while the bot went on trading the compiled-in defaults. Fire-and-
 * forget — a failed push leaves the chart correct and the engine on its last
 * saved rule, which is visible in the engine's own settings endpoint rather
 * than silently wrong.
 */
async function pushThresholds(t: CandidateThresholds): Promise<boolean> {
  try {
    const res = await fetch(
      `${CONFIG.getBackendUrl(window.location.hostname)}/api/diamond-settings`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(t),
      }
    );
    if (!res.ok) {
      console.error('[DIAMONDS] engine rejected settings', res.status, await res.text());
      return false;
    }
    const body = (await res.json()) as { success?: boolean; error?: string };
    if (body && body.success === false) {
      // The engine validates and can refuse — a partial or malformed rule is
      // rejected outright. Silence here is how the chart and the bot end up
      // scoring with different rules, so this is loud.
      console.error('[DIAMONDS] engine rejected settings:', body.error);
      return false;
    }
    return true;
  } catch (e) {
    console.error('[DIAMONDS] could not reach engine to save settings', e);
    return false;
  }
}

/**
 * Read the diamond bot's minConfidence so the chart can show what it would
 * take. Read-only: the bot owns this number, we only mirror it.
 */
export async function hydrateBotThresholdFromEngine(): Promise<void> {
  try {
    const res = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/auto-bot/bots`);
    if (!res.ok) return;
    const body = (await res.json()) as { bots?: Array<Record<string, unknown>> };
    const bot = (body.bots ?? []).find(
      (b) => (b.strategyName as string) === 'Diamonds' && b.enabled === true
    );
    // SLICES FIRST. The migration to a sliced bot clears `minConfidence` so
    // the bands live in one place, which left this reading null and the chart
    // showing no bot threshold at all. The floor is the lowest ENABLED slice:
    // anything under it is taken by nothing.
    const slices = (bot?.slices ?? []) as Array<{
      minScore?: number;
      enabled?: boolean;
    }>;
    const active = slices.filter((sl) => sl.enabled !== false && typeof sl.minScore === 'number');

    let min: number | null = null;
    if (active.length > 0) {
      min = Math.min(...active.map((sl) => sl.minScore as number));
    } else {
      const filter = bot?.signalFilter as { minConfidence?: number } | undefined;
      min = typeof filter?.minConfidence === 'number' ? filter.minConfidence : null;
    }
    useCandidateStore.setState({ botMinConfidence: min });
  } catch {
    // engine down — leave it unknown rather than guessing a threshold
  }
}

/**
 * Adopt the engine's saved rule. The ONLY way `thresholds` becomes non-null.
 *
 * The server's object is taken verbatim. It used to be spread over a local
 * default set "to fill fields an older engine has not heard of yet", which
 * meant any field the engine omitted was silently supplied by a constant that
 * had drifted from it — the chart then displayed, and could push back, numbers
 * the engine had never said. Every one of those fields is `#[serde(default)]`
 * on the Rust side, so the engine always sends a complete rule; if it ever
 * doesn't, leaving `thresholds` null shows a placeholder instead of a fiction.
 */
export async function hydrateThresholdsFromEngine(): Promise<void> {
  try {
    const res = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/diamond-settings`);
    if (!res.ok) return;
    const remote = (await res.json()) as Partial<CandidateThresholds>;
    if (!remote || typeof remote !== 'object') return;
    // A partial rule is not a rule. Every numeric field has to be there or the
    // panel would render `undefined` into an input and POST it back.
    const required: (keyof CandidateThresholds)[] = [
      'childBodyMaxPctOfParentBody',
      'childBodyMinPctOfParentBody',
      'parentBodyMinPct',
      'atrMult',
      'atrPeriod',
      'minAtr',
      'hodLodRangePoints',
      'hodLodPenalty',
      'superStrongScore',
      'strongScore',
      'aboveModerateScore',
      'moderateScore',
    ];
    if (required.some((k) => typeof remote[k] !== 'number')) {
      console.error('[DIAMONDS] engine returned an incomplete rule; ignoring', remote);
      return;
    }
    useCandidateStore.setState({ thresholds: remote as CandidateThresholds });
  } catch {
    // offline or engine down — leave the rule unknown rather than guessing one
  }
}

function persist(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch {
    // storage blocked — non-fatal
  }
}

/**
 * One-time correction of the STORED view window, run before the store reads it.
 *
 * Changing the default lookback on its own fixes nothing: the default is only
 * consulted when the key is ABSENT, and every browser that has ever opened this
 * panel has a value written. That is the exact spot where the previous fix to
 * "cant see diamonds" died — the default moved and no existing browser noticed.
 *
 * So the stored window is rewritten to the full loaded range once, then stamped.
 * It runs ONCE. Anything picked after it is the user's answer and is never
 * touched again, because a migration that fired on every load would silently
 * undo every preset click and make the presets decorative.
 *
 * The rewrite is unconditional rather than only-if-narrower: after it, every
 * browser is in the same state, which is one state to reason about instead of
 * two.
 */
export function migrateLookbackWindow(): void {
  try {
    const stamped = parseInt(localStorage.getItem(LOOKBACK_MIGRATION_KEY) ?? '', 10);
    const done = Number.isFinite(stamped) ? stamped : 0;
    if (done >= LOOKBACK_MIGRATION_VERSION) return;

    localStorage.setItem(LOOKBACK_KEY, String(LOOKBACK_ALL));

    // A pinned cutoff ignores lookbackDays entirely, so widening the window
    // leaves a pinned browser exactly as empty as it was — the same bug wearing
    // a different hat. A pin set moments ago is a real "score from here" and
    // survives; a stale one is dropped so the widened window actually applies.
    if (localStorage.getItem(SCORE_FROM_PINNED_KEY) === 'true') {
      const pinnedAt = parseInt(localStorage.getItem(SCORE_FROM_KEY) ?? '', 10);
      const fresh = Number.isFinite(pinnedAt) && Date.now() - pinnedAt <= PIN_FRESH_MS;
      if (!fresh) {
        localStorage.setItem(SCORE_FROM_PINNED_KEY, 'false');
        // Cleared too, so storage never shows a cutoff that is not in force.
        localStorage.setItem(SCORE_FROM_KEY, '0');
      }
    }

    localStorage.setItem(LOOKBACK_MIGRATION_KEY, String(LOOKBACK_MIGRATION_VERSION));
  } catch {
    // Storage blocked. Nothing to migrate — with no stored value the defaults
    // below are already the full range.
  }
}

// Before the store below reads any of these keys. Import order is the ordering
// guarantee: the initialisers run inside the create() call further down.
migrateLookbackWindow();

/**
 * Key for `engineDiamonds` and `engineRejected`. One verdict per bar per symbol.
 *
 * The same key the labels use, so a bar's diamond, its rejection and your call
 * on it are all reachable from one id and cannot drift apart.
 */
export const engineDiamondKey = candidateId;

/** How far the chart may ask back. See `fetchHistory`. */
export interface HistoryRequest {
  symbol: string;
  /**
   * CME sessions. The engine anchors this on the newest BAR, not the clock, and
   * caps it at however many sessions actually have candles.
   */
  days: number;
  /**
   * Newest COMPLETED bar the chart held when the request went out.
   *
   * The merge rule turns on this: the response is authoritative for everything
   * at or before it, and any diamond the socket delivered after it survives.
   * Without it a bar that completed while the fetch was in flight would be
   * erased by a snapshot taken before it existed.
   */
  throughMs: number;
}

/** What the last history fetch covered, straight off the response. */
export interface HistoryCoverage {
  symbol: string;
  /** Sessions actually served — less than `daysRequested` means history ran out. */
  days: number;
  daysRequested: number;
  fromMs: number | null;
  candlesInWindow: number;
  /**
   * Of those, the ones with a trailing bar — the ones that could produce a
   * verdict at all. `candlesInWindow - candlesJudged` is the blind spot, and
   * with no local scorer left it is the difference between "no diamond here"
   * and "nobody looked here".
   */
  candlesJudged: number;
  /**
   * Newest bar the snapshot is authoritative for — the `throughMs` the fetch
   * went out with, not a field of the response.
   *
   * The OTHER edge of the window. `fromMs` says how far back the engine was
   * asked; without this there is nothing saying where the asking stopped, and a
   * bar that completed after the fetch is indistinguishable from one the engine
   * was asked about and said nothing for. They are different answers: the fetch
   * is deliberately not re-issued per bar (the `diamond-signal` socket is there
   * for that) and the socket carries HITS only, so past this timestamp a blank
   * bar means "no diamond has been reported", while inside the window it means
   * the response had nothing for a bar it covered — which should not happen and
   * is worth being able to say so.
   */
  throughMs: number;
}

export type HistoryStatus = 'idle' | 'loading' | 'ready' | 'error';

interface CandidateStoreState {
  enabled: boolean;
  /**
   * The engine's rule, or null until `/api/diamond-settings` answers.
   *
   * Null rather than a local default object. The default that used to sit here
   * shipped `hodLodRangePoints: 50 / penalty: 25` against the engine's 15 / 20,
   * so a browser that had not heard back yet displayed — and pushed back — a
   * rule nothing was trading. There is no client-side copy of the rule any more;
   * the panel renders a placeholder until the engine answers.
   */
  thresholds: CandidateThresholds | null;
  /**
   * Diamonds the ENGINE scored, keyed `${symbol}:${timestamp}`.
   *
   * The only source of a diamond on this chart. There is no local scorer and no
   * fallback: a bar with no entry here has no score, and the chart draws nothing
   * for it rather than an estimate.
   *
   * Two writers, one key space, so a bar can never be drawn twice:
   *   - `recordEngineDiamond` — the live `diamond-signal` socket event
   *   - `applyHistory`        — `GET /api/diamonds/history`
   *
   * They are the same object: the endpoint calls the same `detect_candidates`
   * and serialises through the same `with_symbol` helper the emit uses.
   */
  engineDiamonds: Record<string, EngineDiamond>;
  /**
   * Bars the engine judged and THREW OUT, keyed the same way.
   *
   * Fetched with `includeRejected=true` and used for two things, both of which
   * used to require re-running the rule locally:
   *   - a bar you hand-labelled `missed` resurfaces in the review list with the
   *     condition it actually lost on
   *   - the on-chart overlay can answer "why is this bar NOT a diamond"
   *
   * Live-empty: `diamond-signal` only fires on hits, so a bar that completes
   * while the tab is open and fails the rule is absent here until the next
   * fetch. That is visible rather than papered over — the overlay says the bar
   * is outside what the engine was asked about.
   */
  engineRejected: Record<string, EngineRejectedBar>;
  /** Where the last `/api/diamonds/history` call got to. */
  historyStatus: HistoryStatus;
  /** Why it failed, for the panel. Null unless `historyStatus === 'error'`. */
  historyError: string | null;
  /** What that call covered, so the chart can explain an empty stretch. */
  historyCoverage: HistoryCoverage | null;
  /** Suggest verdicts for unlabelled candidates from the hand-labelled ones. */
  autoTagEnabled: boolean;
  autoTagOptions: AutoTagOptions;
  labels: CandidateLabelMap;
  /** Candidate id currently open in the review panel, null when none. */
  selectedId: string | null;
  /**
   * Whether the side panel is showing. Off by default — labelling happens
   * inline on the chart; the panel is for thresholds, auto-tag and the score.
   */
  panelOpen: boolean;
  /**
   * Only score candles at or after this time (ms epoch).
   *
   * The baseline is established, so there's no reason to re-mark history — it
   * just clutters the chart. Set on first enable and resettable to "now".
   */
  scoreFrom: number;
  /**
   * True only when "reset to now" set an absolute cutoff. Otherwise the cutoff
   * is a WINDOW and the chart anchors it to the newest bar it actually has.
   */
  scoreFromPinned: boolean;
  /**
   * Bumped by "recalc". The review set is a useMemo over the rule and the
   * candles; when the rule is refetched and comes back IDENTICAL, nothing in
   * the deps changes and nothing redraws. This gives the button something to
   * change so a recalc is never a no-op.
   */
  recalcNonce: number;
  /**
   * The diamond bot's minConfidence, or null when there is no such bot.
   *
   * Held here purely so the chart can show which bars the bot would take. The
   * bot is the authority; this is a read-only mirror and is never pushed back.
   */
  botMinConfidence: number | null;
  /**
   * How far back to score, in days. 0 = today only, -1 = everything loaded.
   * Takes precedence over `scoreFrom` unless you pin it with "reset to now".
   */
  lookbackDays: number;

  setEnabled: (enabled: boolean) => void;
  setPanelOpen: (open: boolean) => void;
  /** Move the cutoff to now, clearing the older markers off the chart. */
  scoreFromNow: () => void;
  setLookbackDays: (days: number) => void;
  setThresholds: (partial: Partial<CandidateThresholds>) => void;
  setAutoTagEnabled: (enabled: boolean) => void;
  setAutoTagOptions: (partial: Partial<AutoTagOptions>) => void;
  /** Promote a suggestion to a real hand label. */
  acceptAutoTag: (id: string, verdict: CandidateVerdict, reason: string) => void;
  /** Discard local edits and take the engine's saved rule. */
  reloadThresholds: () => Promise<void>;
  select: (id: string | null) => void;
  /** Record a diamond the engine scored, as broadcast on `diamond-signal`. */
  recordEngineDiamond: (d: EngineDiamond) => void;
  /**
   * Pull engine-scored history for a symbol and merge it in.
   *
   * Safe to call concurrently: a response is dropped if another request was
   * issued after it, so a symbol switch mid-flight can never repaint the new
   * chart with the old symbol's diamonds.
   */
  fetchHistory: (req: HistoryRequest) => Promise<void>;
  /** Re-pull the rule from the engine, then force a redraw. */
  recalculate: () => Promise<void>;

  /** Label a candidate. Passing the same verdict again clears it (toggle off). */
  label: (id: string, verdict: CandidateVerdict, reason?: string) => void;
  clearLabel: (id: string) => void;
  clearAllLabels: () => void;

  /** Pull labels out of the persisted preferences on startup. */
  hydrateFromPreferences: () => void;
}

/**
 * Monotonic request counter, so only the NEWEST history fetch may write.
 *
 * Module-level rather than in the store: it is not state anyone renders, and
 * putting it in the store would make every in-flight request a re-render.
 */
let historySeq = 0;

/**
 * Merge one history response into the diamond maps.
 *
 * ── The merge rule ────────────────────────────────────────────────────────
 *
 * The two sources cannot produce two markers for one bar: both key on
 * `${symbol}:${timestamp}`, so a bar is one entry by construction.
 *
 * What they CAN do is disagree, and there is exactly one innocent way that
 * happens — the rule was retuned between the live emit and this fetch. The
 * socket diamond was scored under the old thresholds and is now stale; the
 * response was scored under the current ones. So:
 *
 *   - the response is AUTHORITATIVE for every bar at or before `throughMs`
 *   - a socket diamond after `throughMs` survives, because the snapshot was
 *     taken before that bar existed
 *   - a bar in the window that the response did NOT return is dropped, even if
 *     the socket had one. That is the engine saying it is no longer a hit under
 *     the current rule, and keeping the old marker would show a diamond the bot
 *     would not take.
 *
 * A disagreement is logged with both numbers rather than silently resolved: if
 * it happens without a settings change, the two sources have come apart and
 * that is the whole failure this subsystem exists to prevent.
 */
function applyHistory(
  s: { engineDiamonds: Record<string, EngineDiamond>; engineRejected: Record<string, EngineRejectedBar> },
  symbol: string,
  throughMs: number,
  body: DiamondHistoryResponse
): Partial<CandidateStoreState> {
  const diamonds: Record<string, EngineDiamond> = {};
  const rejected: Record<string, EngineRejectedBar> = {};

  // Other symbols are untouched, and so is anything newer than the snapshot.
  for (const [key, d] of Object.entries(s.engineDiamonds)) {
    if (d.symbol !== symbol || d.timestamp > throughMs) diamonds[key] = d;
  }
  for (const [key, r] of Object.entries(s.engineRejected)) {
    if (r.symbol !== symbol || r.timestamp > throughMs) rejected[key] = r;
  }

  for (const d of body.diamonds) {
    if (!d?.symbol || typeof d.timestamp !== 'number') continue;
    const key = candidateId(d.symbol, d.timestamp);
    const live = s.engineDiamonds[key];
    if (live && (Math.abs(live.score - d.score) > 0.05 || live.grade !== d.grade)) {
      console.warn(
        `[DIAMONDS] ${d.symbol} @ ${new Date(d.timestamp).toLocaleTimeString()}: ` +
          `live said ${live.score.toFixed(1)} ${live.grade}, history says ` +
          `${d.score.toFixed(1)} ${d.grade} — taking history. ` +
          'expected only after the rule was retuned; otherwise the two engine ' +
          'paths have come apart'
      );
    }
    diamonds[key] = d;
    // A bar is accepted or rejected, never both.
    delete rejected[key];
  }

  for (const r of body.rejected ?? []) {
    if (!r?.symbol || typeof r.timestamp !== 'number') continue;
    const key = candidateId(r.symbol, r.timestamp);
    if (diamonds[key]) continue;
    rejected[key] = r;
  }

  return {
    engineDiamonds: diamonds,
    engineRejected: rejected,
    historyStatus: 'ready',
    historyError: null,
    historyCoverage: {
      symbol: body.symbol,
      days: body.days,
      daysRequested: body.daysRequested,
      fromMs: body.fromMs,
      candlesInWindow: body.candlesInWindow,
      candlesJudged: body.candlesJudged,
      // The caller's snapshot boundary, carried through so the chart can tell
      // "the engine was asked about this bar" from "this bar is newer than the
      // last ask". Same value the merge rule above uses, deliberately — two
      // notions of where the snapshot ends is how they come apart.
      throughMs,
    },
  };
}

/** Write the whole label map back to the server (merged by the prefs layer). */
function persistLabels(labels: CandidateLabelMap): void {
  try {
    updatePreferenceDirect('setupLabels', labels as never);
  } catch (err) {
    console.warn('[Candidates] failed to persist labels', err);
  }
}

export const useCandidateStore = create<CandidateStoreState>((set, get) => ({
  enabled: loadEnabled(),
  // Defaults only until the engine answers. The rule lives SERVER SIDE: the
  // bot trades it, so a browser must not be able to hold a different copy.
  //
  // Reading localStorage here is what let a stale browser value survive across
  // sessions and get pushed back over the server's, so the chart and the bot
  // silently scored with different rules.
  thresholds: (() => {
    dropLegacyThresholdCache();
    // Null until `/api/diamond-settings` answers. A guessed rule on screen is
    // indistinguishable from the real one and is how a browser came to disagree
    // with the bot in the first place.
    return null;
  })(),
  engineDiamonds: {},
  engineRejected: {},
  historyStatus: 'idle',
  historyError: null,
  historyCoverage: null,
  autoTagEnabled: (() => {
    try { return localStorage.getItem(AUTOTAG_ON_KEY) === 'true'; } catch { return false; }
  })(),
  autoTagOptions: (() => {
    try {
      const raw = localStorage.getItem(AUTOTAG_KEY);
      return raw
        ? { ...DEFAULT_AUTO_TAG_OPTIONS, ...(JSON.parse(raw) as Partial<AutoTagOptions>) }
        : { ...DEFAULT_AUTO_TAG_OPTIONS };
    } catch { return { ...DEFAULT_AUTO_TAG_OPTIONS }; }
  })(),
  labels: {},
  selectedId: null,
  panelOpen: (() => {
    try { return localStorage.getItem(PANEL_KEY) === 'true'; } catch { return false; }
  })(),
  lookbackDays: (() => {
    try {
      // Default -1: the whole loaded range.
      //
      // 0 cuts at midnight today, so out of hours there are no bars after the
      // cutoff and the chart draws nothing while explaining nothing. 1 was the
      // patch for that and still hid every bar older than a day even though the
      // chart holds three and scores all of them. The only default that cannot
      // produce an unexplained empty chart is "draw what you have"; narrowing
      // stays one preset click away.
      const raw = localStorage.getItem(LOOKBACK_KEY);
      const v = raw === null ? LOOKBACK_ALL : parseInt(raw, 10);
      return Number.isFinite(v) ? v : LOOKBACK_ALL;
    } catch { return LOOKBACK_ALL; }
  })(),
  /**
   * Derived from `lookbackDays` on every load, NOT read back as a stored
   * absolute — unless "reset to now" pinned one.
   *
   * It used to restore whatever timestamp was last persisted. That timestamp
   * ages: a cutoff written at midnight is still midnight tomorrow, and out of
   * hours there are no bars after it, so the chart draws nothing and says
   * nothing. Reported as "cant see diamonds anymore" and it survived the first
   * fix, because changing the DEFAULT lookback does nothing for a browser that
   * already has a stale absolute stored.
   *
   * The type has always documented lookbackDays as taking precedence. It just
   * never did on load.
   */
  recalcNonce: 0,
  botMinConfidence: null,
  scoreFromPinned: (() => {
    try { return localStorage.getItem(SCORE_FROM_PINNED_KEY) === 'true'; }
    catch { return false; }
  })(),
  scoreFrom: (() => {
    try {
      const pinned = localStorage.getItem(SCORE_FROM_PINNED_KEY) === 'true';
      if (pinned) {
        const raw = localStorage.getItem(SCORE_FROM_KEY);
        const v = raw ? parseInt(raw, 10) : NaN;
        if (Number.isFinite(v)) return v;
      }
      const rawDays = localStorage.getItem(LOOKBACK_KEY);
      const days = rawDays === null ? LOOKBACK_ALL : parseInt(rawDays, 10);
      const d = Number.isFinite(days) ? days : LOOKBACK_ALL;
      return d < 0 ? 0 : startOfToday() - d * 86_400_000;
    } catch {
      // Matches the lookbackDays fallback above: no storage, full range.
      return 0;
    }
  })(),

  setEnabled: (enabled) => {
    persist(ENABLED_KEY, String(enabled));
    // Turning it on scores today rather than re-marking the whole history.
    if (enabled) {
      // Honour whatever lookback is already set rather than snapping to today.
      const days = get().lookbackDays;
      const from = days < 0 ? 0 : startOfToday() - days * 86_400_000;
      persist(SCORE_FROM_PINNED_KEY, 'false');
      persist(SCORE_FROM_KEY, String(from));
      set({ enabled, scoreFrom: from, scoreFromPinned: false });
      return;
    }
    set({ enabled });
  },

  setThresholds: (partial) => {
    const current = get().thresholds;
    // Nothing to edit until the engine has told us what the rule IS. Merging a
    // partial into null would invent the other twelve fields.
    if (!current) return;
    const next = { ...current, ...partial };
    // Optimistic so the UI stays responsive, then reconciled against the
    // engine below. If the engine refuses, we snap back to ITS value rather
    // than leaving the chart showing a rule nothing is trading.
    set({ thresholds: next });
    void pushThresholds(next).then((ok) => {
      if (!ok) void hydrateThresholdsFromEngine();
    });
  },

  // "Reset" now means "take the engine's saved rule", not "take the constants
  // this file used to carry". Those constants are gone, and they had drifted
  // from the engine's defaults anyway — resetting to them moved the chart
  // FURTHER from what the bot trades.
  reloadThresholds: () => hydrateThresholdsFromEngine(),

  setLookbackDays: (days) => {
    persist(LOOKBACK_KEY, String(days));
    // Choosing a lookback un-pins: you asked for a window, not an instant.
    persist(SCORE_FROM_PINNED_KEY, 'false');
    // -1 means everything loaded; otherwise walk back N whole days from today.
    const from = days < 0 ? 0 : startOfToday() - days * 86_400_000;
    persist(SCORE_FROM_KEY, String(from));
    set({ lookbackDays: days, scoreFrom: from, scoreFromPinned: false });
  },

  scoreFromNow: () => {
    const now = Date.now();
    // The ONE case where an absolute cutoff should survive a reload: you
    // deliberately said "from here". Everything else derives from lookback.
    persist(SCORE_FROM_PINNED_KEY, 'true');
    persist(SCORE_FROM_KEY, String(now));
    set({ scoreFrom: now, scoreFromPinned: true });
  },

  setPanelOpen: (panelOpen) => {
    persist(PANEL_KEY, String(panelOpen));
    set({ panelOpen });
  },

  setAutoTagEnabled: (autoTagEnabled) => {
    persist(AUTOTAG_ON_KEY, String(autoTagEnabled));
    set({ autoTagEnabled });
  },

  setAutoTagOptions: (partial) => {
    const next = { ...get().autoTagOptions, ...partial };
    persist(AUTOTAG_KEY, next);
    set({ autoTagOptions: next });
  },

  // Accepting a suggestion writes a REAL label. Suggestions are never persisted
  // on their own, so this is the only path from prediction to ground truth.
  acceptAutoTag: (id, verdict, reason) => get().label(id, verdict, reason),

  select: (selectedId) => set({ selectedId }),

  fetchHistory: async ({ symbol, days, throughMs }) => {
    if (!symbol) return;
    const seq = ++historySeq;
    set({ historyStatus: 'loading', historyError: null });

    try {
      const url =
        `${CONFIG.getBackendUrl(window.location.hostname)}/api/diamonds/history` +
        `?symbol=${encodeURIComponent(symbol)}&days=${Math.max(1, Math.ceil(days))}` +
        // Rejected bars are what let a hand-labelled `missed` bar resurface
        // with the condition it lost on, and what lets the overlay say why a
        // bar is NOT a diamond. Both used to be answered by re-running the rule
        // locally, so this flag is load-bearing rather than diagnostic.
        `&includeRejected=true`;
      const res = await fetch(url);

      // A stale response must never repaint. Checked before the body is even
      // read: a symbol switch during a slow fetch would otherwise drop the old
      // symbol's diamonds onto the new chart.
      if (seq !== historySeq) return;

      if (!res.ok) {
        // 404 is the engine saying it does not watch this symbol — a real
        // answer, and a different one from "the request failed". Both leave the
        // chart blank; only one is worth a retry.
        const detail =
          res.status === 404
            ? `engine does not watch ${symbol}`
            : `engine returned ${res.status}`;
        set({ historyStatus: 'error', historyError: detail, historyCoverage: null });
        console.error('[DIAMONDS] history fetch failed:', detail);
        return;
      }

      const body = (await res.json()) as DiamondHistoryResponse;
      if (seq !== historySeq) return;
      if (!body || !Array.isArray(body.diamonds)) {
        set({
          historyStatus: 'error',
          historyError: 'engine sent a response with no diamonds array',
          historyCoverage: null,
        });
        return;
      }

      set((s) => applyHistory(s, symbol, throughMs, body));
    } catch (e) {
      if (seq !== historySeq) return;
      const detail = e instanceof Error ? e.message : 'could not reach the engine';
      set({ historyStatus: 'error', historyError: detail, historyCoverage: null });
      console.error('[DIAMONDS] history fetch failed:', e);
    }
  },

  recalculate: async () => {
    await hydrateBotThresholdFromEngine();
    // The rule lives server-side, so a recalc is "go and ask": anything
    // changed via the API or another browser lands here.
    await hydrateThresholdsFromEngine();
    // The nonce is what the chart watches to re-issue its history fetch. It
    // cannot be issued from here: this store does not know which symbol is
    // charted or how far back its candles go, and guessing either would fetch
    // a window that does not line up with the bars on screen.
    set((s) => ({ recalcNonce: s.recalcNonce + 1 }));
  },

  recordEngineDiamond: (d) =>
    set((s) => {
      const key = engineDiamondKey(d.symbol, d.timestamp);
      // A live hit for a bar an earlier fetch listed as rejected means the rule
      // changed under us. The bar is a hit now, so the stale rejection goes —
      // leaving it would let the review list show one bar as both.
      const rejected = s.engineRejected[key] ? { ...s.engineRejected } : s.engineRejected;
      if (rejected !== s.engineRejected) delete rejected[key];
      return {
        engineDiamonds: { ...s.engineDiamonds, [key]: d },
        engineRejected: rejected,
      };
    }),

  label: (id, verdict, reason = '') => {
    const current = get().labels;
    const existing = current[id];

    // Clicking the same verdict again un-labels it, so a mis-click is one
    // click to undo rather than a stuck label.
    if (existing && existing.verdict === verdict && existing.reason === reason) {
      get().clearLabel(id);
      return;
    }

    const next: CandidateLabelMap = {
      ...current,
      [id]: { verdict, reason, at: Date.now() } as CandidateLabel,
    };
    set({ labels: next });
    persistLabels(next);
  },

  clearLabel: (id) => {
    const next = { ...get().labels };
    delete next[id];
    set({ labels: next });
    persistLabels(next);
  },

  clearAllLabels: () => {
    set({ labels: {} });
    persistLabels({});
  },

  hydrateFromPreferences: () => {
    try {
      const prefs = getPreferences() as Record<string, unknown>;
      const saved = prefs?.setupLabels;
      if (saved && typeof saved === 'object') {
        // Old labels used "ideal"; map them onto the current grades so a
        // pre-existing pass of labelling isn't silently dropped.
        const migrated: CandidateLabelMap = {};
        for (const [id, l] of Object.entries(saved as Record<string, CandidateLabel>)) {
          migrated[id] = { ...l, verdict: normaliseVerdict(String(l.verdict)) };
        }
        set({ labels: migrated });
      }
    } catch {
      // preferences not ready yet — the caller retries
    }
  },
}));

// ============================================================================
// Selectors
// ============================================================================

export const useCandidatesEnabled = (): boolean => useCandidateStore((s) => s.enabled);

export const useCandidateThresholds = (): CandidateThresholds | null =>
  useCandidateStore(useShallow((s) => s.thresholds));

export const useCandidateLabels = (): CandidateLabelMap =>
  useCandidateStore(useShallow((s) => s.labels));

export const useSelectedCandidateId = (): string | null =>
  useCandidateStore((s) => s.selectedId);
